import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert02Icon,
  CheckmarkCircle02Icon,
  InformationCircleIcon,
} from '@hugeicons-pro/core-solid-rounded';
import { Loading03Icon, MinusSignCircleIcon, Passport01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { IDV_OVERALL_STATUS, MAX_RETRIES } from 'constants/idv';
import { ALLOWED_TYPES_MODE_ID } from 'constants/upload-allowed-types';
import { useDisableCandidateFormActions } from 'contexts/contexts';
import DOMPurify from 'dompurify';
import {
  isAcceptedUpload,
  isDualOptionSatisfied,
} from 'helpers/candidate_application/id-requirements';
import { matchesRequired, parseIdKey } from 'helpers/ids';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Accordion } from 'components/Accordion/Accordion';
import { DocumentUpload } from 'components/FormComponents/DocumentUpload/DocumentUpload';
import { DocumentUploadWithFields } from 'components/FormComponents/DocumentUpload/DocumentUploadWithFields';
import { MultipleDocumentUpload } from 'components/FormComponents/MultipleDocumentUpload/MultipleDocumentUpload';
import { StatusPill } from 'components/Pills/StatusPills';

import { selectCandidateApplication } from 'api/candidate-application-slice';

import styles from './IdRequirementsList.module.scss';

const LIST_STATUS = {
  COMPLETE: 'complete',
  IN_PROGRESS: 'in_progress',
  ACTION_NEEDED: 'action_needed',
  OPTIONAL: 'optional',
};

const LIST_STATUS_CONFIG = {
  [LIST_STATUS.COMPLETE]: {
    borderClass: 'border-green-2',
    icon: CheckmarkCircle02Icon,
    iconClass: 'text-green-2',
    subtitleClass: 'text-green-2',
    pillStatus: 'success',
    pillText: 'Complete',
    pillBorder: false,
  },
  [LIST_STATUS.IN_PROGRESS]: {
    borderClass: 'border-primary-4',
    icon: MinusSignCircleIcon,
    iconClass: 'text-primary-4',
    subtitleClass: 'text-primary-4',
    pillStatus: 'brand',
    pillText: 'In Progress',
    pillBorder: false,
  },
  [LIST_STATUS.ACTION_NEEDED]: {
    borderClass: 'border-orange-3',
    icon: Alert02Icon,
    iconClass: 'text-orange-3',
    subtitleClass: 'text-orange-3',
    pillStatus: 'warning',
    pillText: 'Action Required',
    pillBorder: false,
  },
  [LIST_STATUS.OPTIONAL]: {
    borderClass: 'border-neutral-4',
    icon: MinusSignCircleIcon,
    iconClass: 'text-neutral-6',
    subtitleClass: 'text-primary-4',
    pillStatus: 'light',
    pillText: 'Optional',
    pillBorder: true,
  },
};

const uploadBelongsToList = (upload, listCode, option = null) => {
  const uploadListCode =
    upload?.id_upload_detail?.id_requirements_list ??
    upload?.metadata?.id_requirements_list ??
    null;
  if (uploadListCode == null || uploadListCode === listCode) return true;
  // Server-side pool-assign can credit an upload to this list even though it's raw-owned
  // elsewhere — normally that renders as "satisfied elsewhere" (option.creditedFromAnotherList)
  // and this returns false so the real component stays with its raw owner. But once that raw
  // owner list has gone invisible (e.g. citizenship changing so it no longer applies),
  // id-requirements.js flips creditedFromAnotherList back to false precisely because this list
  // is now the only place left to manage it — treat it as belonging here too in that case.
  return !!(option?.selected && !option?.creditedFromAnotherList);
};

const optionShowsConfirmDetails = (option, upload) => {
  if (!upload?.id_upload_detail) return false;
  if (upload?.metadata?.dual === 'back') return false;
  if (upload.id_upload_detail.confirmed_by_cand) return false;

  const displayFields = option?.inputFields ?? option?.input_fields ?? [];
  if (!Array.isArray(displayFields) || displayFields.length === 0) return false;

  const status = upload.id_upload_detail.status;
  const attempts = upload?.metadata?.attempts || 0;
  return (
    status === IDV_OVERALL_STATUS.VERIFIED ||
    status === IDV_OVERALL_STATUS.NEEDS_REVIEW ||
    status === IDV_OVERALL_STATUS.NOT_APPLICABLE ||
    (status === IDV_OVERALL_STATUS.FAILED && attempts >= MAX_RETRIES)
  );
};

const optionHasError = (option, idUploads, rejectedData, hasPendingConfirmation, listCode) => {
  if (option.needsComplementaryDoc) return true;

  const uploads =
    idUploads?.filter(
      upload =>
        matchesRequired(upload?.metadata?.id_type, option.value) &&
        uploadBelongsToList(upload, listCode, option),
    ) || [];
  if (uploads.some(upload => rejectedData?.files?.includes(upload.id))) return true;

  if (
    uploads.some(upload => {
      const status = upload?.id_upload_detail?.status;
      return (
        status === IDV_OVERALL_STATUS.REJECTED ||
        (status === IDV_OVERALL_STATUS.FAILED && !isAcceptedUpload(upload))
      );
    })
  ) {
    return true;
  }

  // Extracted fields awaiting "Confirm details" — match DocumentUpload visibility rules
  if (uploads.some(upload => optionShowsConfirmDetails(option, upload))) {
    return true;
  }

  // Dual document with an accepted front but an incomplete pair (back missing, not
  // force-accepted, and still genuinely required per backConditions) needs the candidate to
  // finish it — independent of whether this option even has extracted fields to confirm.
  if (
    option?.dual === true &&
    uploads.some(
      upload =>
        upload?.metadata?.dual !== 'back' &&
        isAcceptedUpload(upload) &&
        !isDualOptionSatisfied(upload, option),
    )
  ) {
    return true;
  }

  // Live session: ExtractedFieldsDisplay has registered a pending confirmation for this upload
  if (
    typeof hasPendingConfirmation === 'function' &&
    uploads.some(upload => hasPendingConfirmation(upload?.id))
  ) {
    return true;
  }

  return false;
};

export const IdRequirementsList = ({
  code,
  uploadCode,
  brand,
  options = [],
  className,
  wrapperRequirementsMet = false,
  hasIdRequirements = false,
  requirementsMet = false,
  collapsible = false,
  getUpload,
  signUpload,
  createUpload,
  updateUpload,
  deleteUpload,
  modelParams,
  min = 1,
  title,
  description,
  onPointsChange,
  setValue,
  rejectedData,
}) => {
  const { t } = useTranslation();
  const {
    candidateFields,
    application: { id_uploads, other_uploads },
  } = useSelector(selectCandidateApplication);
  const { hasPendingConfirmation } = useDisableCandidateFormActions();

  // Store the latest callback in a ref to avoid re-render loops
  const onPointsChangeRef = useRef(onPointsChange);
  onPointsChangeRef.current = onPointsChange;

  const [verifyingOptionValues, setVerifyingOptionValues] = useState(() => new Set());
  const isVerifying = verifyingOptionValues.size > 0;

  // Calculate current points from option.selected (same source of truth as id-requirements.js).
  // This keeps the bar in sync when IDs are assigned from other lists (e.g. pending assignment).
  const currentPoints = useMemo(() => {
    return options.reduce((total, option) => {
      if (!option.selected) return total;
      if (option.needsComplementaryDoc) return total;
      const p = Number.isFinite(option.points) ? option.points : 0;
      return total + p;
    }, 0);
  }, [options]);

  const makeHandleVerifyState = optionValue => state => {
    setVerifyingOptionValues(prev => {
      const next = new Set(prev);
      if (state) next.add(optionValue);
      else next.delete(optionValue);
      return next;
    });
  };

  // Notify parent component when points change
  useEffect(() => {
    if (onPointsChangeRef.current) {
      onPointsChangeRef.current(currentPoints);
    }
  }, [currentPoints]); // Only depend on the actual calculated points value

  // Compute filtered uploads - only include accepted IDs (verified, needs_review, or failed with >= 3 attempts)
  const idsUploadedOnList = useMemo(() => {
    return (
      id_uploads?.filter(upload => {
        const matchesOption = options.some(option =>
          matchesRequired(upload.metadata?.id_type, option.value),
        );
        if (!matchesOption) return false;
        return isAcceptedUpload(upload);
      }) || []
    );
  }, [id_uploads, options]);

  // Use ref to track previous value and prevent unnecessary updates
  const prevUploadedIdsRef = useRef([]);

  // Save uploaded document values to form
  useEffect(() => {
    if (!setValue) return;

    const currentUploadedIds = idsUploadedOnList
      .map(upload => upload.metadata?.id_type)
      .filter(Boolean);

    // Only update if the values have actually changed
    const hasChanged =
      currentUploadedIds.length !== prevUploadedIdsRef.current.length ||
      currentUploadedIds.some((id, index) => id !== prevUploadedIdsRef.current[index]);

    if (hasChanged) {
      setValue(`${code}`, currentUploadedIds);
      prevUploadedIdsRef.current = currentUploadedIds;
    }
  }, [idsUploadedOnList, code, setValue]);

  const selfieId = useMemo(() => {
    return other_uploads?.find(u => u?.metadata?.selfie)?.id;
  }, [other_uploads]);

  const uploadedCount = useMemo(() => options.filter(option => option.selected).length, [options]);

  const actionNeededOption = useMemo(
    () =>
      options.find(option =>
        optionHasError(option, id_uploads, rejectedData, hasPendingConfirmation, code),
      ),
    [options, id_uploads, rejectedData, hasPendingConfirmation, code],
  );

  const listHasPoints = useMemo(
    () => options.some(option => Number.isFinite(option.points) && option.points > 0),
    [options],
  );

  const listStatus = useMemo(() => {
    if (actionNeededOption) return LIST_STATUS.ACTION_NEEDED;
    if (!hasIdRequirements) return LIST_STATUS.OPTIONAL;
    if (isVerifying) return LIST_STATUS.IN_PROGRESS;
    // When options award points, Completeness tracks overall points (wrapper), not just this
    // list's min document count — otherwise a 70/100 list would show Complete prematurely.
    if (listHasPoints) {
      return wrapperRequirementsMet ? LIST_STATUS.COMPLETE : LIST_STATUS.IN_PROGRESS;
    }
    if (requirementsMet) return LIST_STATUS.COMPLETE;
    return LIST_STATUS.IN_PROGRESS;
  }, [
    actionNeededOption,
    hasIdRequirements,
    requirementsMet,
    wrapperRequirementsMet,
    listHasPoints,
    isVerifying,
  ]);

  const statusConfig = LIST_STATUS_CONFIG[listStatus];

  const statusSubtitle = useMemo(() => {
    if (listStatus === LIST_STATUS.ACTION_NEEDED) {
      return `Action required on ${actionNeededOption?.name || 'document'}`;
    }
    if (listStatus === LIST_STATUS.OPTIONAL) {
      return uploadedCount > 0
        ? `${uploadedCount} uploaded • Additional documents (optional)`
        : 'Additional documents (optional)';
    }
    return `${uploadedCount} uploaded • ${options?.length > 1 ? `Minimum` : ''} ${min || 0} required`;
  }, [listStatus, actionNeededOption, uploadedCount, options.length, min]);

  const listHeader = (
    <div className="flex items-center gap-4 w-full min-w-0">
      <HugeiconsIcon
        icon={statusConfig.icon}
        size={28}
        className={cn('shrink-0', statusConfig.iconClass)}
      />
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <span className="text-[20px] font-medium text-neutral-7 truncate">{title}</span>
        <span className={cn('text-xl font-regular', statusConfig.subtitleClass)}>
          {statusSubtitle}
        </span>
      </div>
      {listStatus !== LIST_STATUS.OPTIONAL && (
        <StatusPill
          text={statusConfig?.pillText}
          status={statusConfig?.pillStatus}
          noBorder={!statusConfig?.pillBorder}
          className="!px-4"
        />
      )}
    </div>
  );

  const optionsList = (
    <div className="w-full flex flex-col border border-neutral-4 rounded-3xl">
      {options.map((option, index) => {
        // Match by option.value so generic options (e.g. "passport") match uploads like "passport:AU"
        // — scoped to this list too, since a generic option in one list and a country-specific
        // option in another can otherwise both claim the same physical upload.
        const matchingUploads =
          id_uploads?.filter(
            u =>
              matchesRequired(u?.metadata?.id_type, option.value) &&
              isAcceptedUpload(u) &&
              uploadBelongsToList(u, code, option),
          ) || [];
        // If this option is generic (no country), don't show an upload that a specific option in this list also matches
        const optionParsed = parseIdKey(option.value);
        const existing =
          optionParsed.countryCode != null
            ? matchingUploads
            : matchingUploads.filter(
                upload =>
                  !options.some(
                    other =>
                      other !== option &&
                      parseIdKey(other.value).countryCode &&
                      matchesRequired(upload?.metadata?.id_type, other.value),
                  ),
              );
        const existingOrNull = existing?.length ? existing : null;

        // Matching uploads for component value props: include pending (not yet accepted) uploads
        // so that DocumentUploadWithFields receives the new upload id and verification can run.
        // isAcceptedUpload excludes new uploads (no id_upload_detail yet), which would make
        // frontValue/backValue null and wipe the component's state, preventing verification.
        const matchingUploadsForComponent =
          id_uploads?.filter(
            u =>
              matchesRequired(u?.metadata?.id_type, option.value) &&
              uploadBelongsToList(u, code, option),
          ) || [];
        const existingForComponent =
          optionParsed.countryCode != null
            ? matchingUploadsForComponent
            : matchingUploadsForComponent.filter(
                upload =>
                  !options.some(
                    other =>
                      other !== option &&
                      parseIdKey(other.value).countryCode &&
                      matchesRequired(upload?.metadata?.id_type, other.value),
                  ),
              );
        const existingOrNullForComponent = existingForComponent?.length
          ? existingForComponent
          : null;

        const hasBypassedFaceSimilarity = id_uploads?.find(
          u =>
            u?.id_upload_detail?.face_similarity_status === 'failed' && u?.metadata?.attempts >= 3,
        );
        const isFileRejected = (existingOrNullForComponent ?? existingOrNull)?.some(file =>
          rejectedData?.files?.includes(file.id),
        );

        // Use existingOrNullForComponent for value props so pending uploads are passed to the component.
        // Identify front/back by the explicit metadata.dual tag set at upload time (not by array
        // position or back_upload_id) — that link is only set server-side after verification and
        // wouldn't exist yet, or would misattribute the remaining upload, if the other side is deleted.
        const frontUpload =
          existingOrNullForComponent?.find(u => u?.metadata?.dual !== 'back') || null;
        const backUpload =
          existingOrNullForComponent?.find(u => u?.metadata?.dual === 'back') || null;
        const hasUpload = Boolean(existingOrNullForComponent?.length);
        const hasError = optionHasError(
          option,
          id_uploads,
          rejectedData,
          hasPendingConfirmation,
          code,
        );
        // Pending field confirmation isn't a genuine error — shown as "Confirmation required" instead.
        const needsConfirmation = matchingUploadsForComponent.some(upload =>
          optionShowsConfirmDetails(option, upload),
        );
        const isUploaded =
          !hasError &&
          (Boolean(option.selected) ||
            (existingOrNullForComponent || []).some(
              upload => isAcceptedUpload(upload) && isDualOptionSatisfied(upload, option),
            ));
        const isBusy = verifyingOptionValues.has(option.value);
        const handleVerifyState = makeHandleVerifyState(option.value);

        const uploadComponent = !option.allowMultipleUploads ? (
          option.dual ? (
            <DocumentUploadWithFields
              uploadCode={uploadCode}
              selfieId={selfieId}
              selfieIdAtLastVerify={frontUpload?.metadata?.selfie_id_at_verify}
              brand={brand}
              type={option.idType}
              selected={option.selected}
              allowedTypesMode={ALLOWED_TYPES_MODE_ID}
              idType={option?.value}
              verifyDocument={option?.verifyDocument !== undefined ? option.verifyDocument : true}
              skipVerification={option?.skipVerification || false}
              reTakeSelfie={
                candidateFields?.hiddenValues?.face_similarity_passed === false &&
                !hasBypassedFaceSimilarity
              }
              documentTitle={option?.name}
              documentDescription={option?.description}
              additionalInstructions={option?.additionalInstructions}
              disabled={option?.disabled}
              countryCode={option?.countryCode}
              backConditions={option?.backConditions}
              expiryTolerance={option?.expiryTolerance}
              frontValue={frontUpload?.id || null}
              backValue={backUpload?.id || null}
              frontAttempts={frontUpload?.metadata?.attempts || 0}
              backAttempts={backUpload?.metadata?.attempts || 0}
              listCode={code}
              metadata={{
                ...(option?.metadata || {}),
                id_type: option?.value,
              }}
              modelParams={modelParams}
              uploadStatus={frontUpload?.id_upload_detail?.status}
              uploadError={frontUpload?.id_upload_detail?.reason_for_failure}
              faceSimilarity={frontUpload?.id_upload_detail?.face_similarity}
              faceSimilarityStatus={frontUpload?.id_upload_detail?.face_similarity_status}
              hasPhoto={frontUpload?.id_upload_detail?.has_photo}
              displayFields={option?.inputFields}
              idValues={(() => {
                const extracted = frontUpload?.id_upload_detail?.extracted_fields;
                return extracted || {};
              })()}
              needsComplementaryDoc={option?.needsComplementaryDoc}
              complementaryInstructions={option?.complementaryInstructions}
              getUpload={getUpload}
              signUpload={signUpload}
              createUpload={createUpload}
              updateUpload={updateUpload}
              deleteUpload={deleteUpload}
              handleStateOnVerify={handleVerifyState}
              caption={option.points ? `${option.points} points` : ''}
              maxSize="5242880"
              uploadId={frontUpload?.id}
              rejected={isFileRejected}
              confirmedByCand={frontUpload?.id_upload_detail?.confirmed_by_cand || false}
            />
          ) : (
            <DocumentUpload
              uploadCode={uploadCode}
              selfieId={selfieId}
              brand={brand}
              type={option.idType}
              selected={option.selected}
              allowedTypesMode={ALLOWED_TYPES_MODE_ID}
              idType={option?.value}
              verifyDocument={option?.verifyDocument !== undefined ? option.verifyDocument : true}
              skipVerification={option?.skipVerification || false}
              reTakeSelfie={
                candidateFields?.hiddenValues?.face_similarity_passed === false &&
                !hasBypassedFaceSimilarity
              }
              maxSize="5242880"
              documentTitle={option?.name}
              documentDescription={option?.description}
              showTitleInUploadSlot={false}
              additionalInstructions={option?.additionalInstructions}
              disabled={option?.disabled}
              countryCode={option?.countryCode}
              expiryTolerance={option?.expiryTolerance}
              value={existingOrNullForComponent?.[0]?.id || null}
              listCode={code}
              metadata={{
                ...(option?.metadata || {}),
                id_type: option?.value,
                attempts: existingOrNullForComponent?.[0]?.metadata?.attempts ?? 0,
              }}
              modelParams={modelParams}
              uploadStatus={existingOrNullForComponent?.[0]?.id_upload_detail?.status}
              uploadError={existingOrNullForComponent?.[0]?.id_upload_detail?.reason_for_failure}
              faceSimilarity={existingOrNullForComponent?.[0]?.id_upload_detail?.face_similarity}
              displayFields={option?.inputFields}
              idValues={(() => {
                const extracted =
                  existingOrNullForComponent?.[0]?.id_upload_detail?.extracted_fields;
                return extracted || {};
              })()}
              needsComplementaryDoc={option?.needsComplementaryDoc}
              complementaryInstructions={option?.complementaryInstructions}
              getUpload={getUpload}
              signUpload={signUpload}
              createUpload={createUpload}
              updateUpload={updateUpload}
              deleteUpload={deleteUpload}
              handleStateOnVerify={handleVerifyState}
              uploadId={existingOrNullForComponent?.[0]?.id}
              rejected={isFileRejected}
              confirmedByCand={
                existingOrNullForComponent?.[0]?.id_upload_detail?.confirmed_by_cand || false
              }
            />
          )
        ) : (
          <MultipleDocumentUpload
            maxSize="5242880"
            brand={brand}
            idType={option?.value}
            allowedTypesMode={ALLOWED_TYPES_MODE_ID}
            metadata={option?.metadata || {}}
            documentTitle={option?.name}
            documentDescription={option?.description}
            additionalInstructions={option?.additionalInstructions}
            disabled={option?.disabled}
            value={existingOrNullForComponent || existingOrNull || []}
            modelParams={modelParams}
            listCode={code}
            verifyDocument={option?.verifyDocument !== undefined ? option.verifyDocument : true}
            skipVerification={option?.skipVerification || false}
            needsComplementaryDoc={option?.needsComplementaryDoc}
            complementaryInstructions={option?.complementaryInstructions}
            getUpload={getUpload}
            signUpload={signUpload}
            createUpload={createUpload}
            updateUpload={updateUpload}
            deleteUpload={deleteUpload}
            handleStateOnVerify={handleVerifyState}
            caption={option.points ? `${option.points} points` : ''}
          />
        );

        if (option?.disabled) return null;

        return (
          <Accordion
            key={option.value}
            dataTestId={`id-requirements-option-${code}-${option.value}`}
            className={cn('w-full p-6', index < options.length - 1 && 'border-b border-neutral-4')}
            defaultCollapsed={hasError ? false : true}
            header={
              <div className="flex items-center justify-between gap-4 w-full">
                <div className="flex items-center gap-4 min-w-0">
                  <span className="relative inline-flex shrink-0">
                    <HugeiconsIcon icon={Passport01Icon} size={20} className="text-neutral-7" />
                    {(isBusy || isUploaded || hasError) && (
                      <HugeiconsIcon
                        icon={
                          isBusy
                            ? Loading03Icon
                            : needsConfirmation
                              ? InformationCircleIcon
                              : hasError
                                ? Alert02Icon
                                : CheckmarkCircle02Icon
                        }
                        size={12}
                        className={cn(
                          'absolute -right-1 -top-1 rounded-full',
                          isBusy && 'bg-white text-primary-6 animate-spin [animation-duration:2s]',
                          !isBusy && needsConfirmation && 'bg-white text-orange-3',
                          !isBusy && !needsConfirmation && hasError && 'bg-white text-red-3',
                          !isBusy && !needsConfirmation && !hasError && 'bg-white text-green-2',
                        )}
                      />
                    )}
                  </span>
                  <span className="text-[16px] font-medium text-neutral-7 truncate">
                    {option.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {needsConfirmation ? (
                    <span className="shrink-0 text-[12px] text-orange-2">
                      {t('components.idRequirementsList.confirmationRequired')}
                    </span>
                  ) : (
                    hasError && (
                      <span className="shrink-0 text-[12px] text-red-3">
                        {t('components.idRequirementsList.actionRequired')}
                      </span>
                    )
                  )}
                  {option?.points && (
                    <StatusPill
                      text={`${option.points} points`}
                      status="neutral"
                      noBorder
                      small
                      className="!px-4"
                    />
                  )}
                </div>
              </div>
            }>
            {option?.creditedFromAnotherList ? (
              <div className="flex items-center gap-2 text-xl text-neutral-6 bg-green-1 rounded-lg p-4 font-medium">
                <HugeiconsIcon icon={CheckmarkCircle02Icon} size={20} className="text-green-2" />
                <span>{t('components.idRequirementsList.satisfiedElsewhere')}</span>
              </div>
            ) : (
              uploadComponent
            )}
          </Accordion>
        );
      })}
    </div>
  );

  const listBody = (
    <div className="flex flex-col gap-4 w-full">
      {description && (
        <span
          className="secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
        />
      )}
      {optionsList}
    </div>
  );

  return (
    <div
      className={cn(
        styles.root,
        'w-full flex flex-col border rounded-3xl border-l-4 border-b-4',
        statusConfig.borderClass,
        { [className]: className },
      )}
      data-testid={`id-requirements-list-${code}`}
      data-status={listStatus}>
      {collapsible ? (
        <Accordion
          dataTestId={`id-requirements-list-toggle-${code}`}
          className="w-full p-6"
          defaultCollapsed={true}
          header={listHeader}>
          {listBody}
        </Accordion>
      ) : (
        <div className="w-full flex flex-col gap-4 p-6">
          {listHeader}
          {listBody}
        </div>
      )}
    </div>
  );
};
